// @flow
import React, { useRef, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import useIntersectionObserver from 'shared/hooks/useIntersectionObserver';
import type { Styles } from 'shared/types';
import { SLIDE_MIN_HEIGHT } from '../Slider';

export type VideoProps = {
  url: ?string,
  mobile_url: ?string,
  cover: ?string
};

type Props = {
  backgroundVideo: VideoProps
};

export default function Video(props: Props) {
  const { backgroundVideo = {} } = props;
  const { cover } = backgroundVideo;

  const c: Styles<typeof styles> = useStyles(props);

  const ref = useRef<HTMLVideoElement | null>(null);
  const isIntersecting = useIntersectionObserver(ref, '0px', 0.1);
  const [isLoadingVideo, setLoadingVideo] = useState(false);

  // Track previous src - only load on change
  const previousVideoSrcRef = useRef<string>('');
  const [videoSrc, setVideoSrc] = useState();

  // Set video URL based on device size
  useEffect(() => {
    const onResize = () => {
      const width = window.innerWidth;

      const newVideoSrc = width < 600 ?
        backgroundVideo?.mobile_url || backgroundVideo?.url :
        backgroundVideo?.url;

      if (newVideoSrc !== previousVideoSrcRef.current) {
        setVideoSrc(newVideoSrc);
        ref.current?.load();
        previousVideoSrcRef.current = newVideoSrc;
      }
    }

    window.addEventListener('resize', onResize);
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [backgroundVideo, previousVideoSrcRef]);

  // Load video if it's in viewpoint
  useEffect(() => {
    if (isIntersecting) {
      setLoadingVideo(true);
    }
  }, [isIntersecting, setLoadingVideo]);

  // Enable autoplay in WeChat browser
  useEffect(() => {
    const play = () => {
      ref?.current?.play();
    };

    document.addEventListener('WeixinJSBridgeReady', play, false);
    // Add handler for Android
    document.addEventListener('click', play, { once: true });

    return () => {
      document.removeEventListener('WeixinJSBridgeReady', play, false);
      document.removeEventListener('click', play, { once: true });
    };
  }, [ref]);

  // Play when visible, pause when not visible
  useEffect(() => {
    if (ref?.current) {
      if (isIntersecting) {
        ref.current.play();
      } else {
        ref.current.pause();
        // Avoid IE11 DOM exception
        if (ref.current.currentTime !== 0) {
          ref.current.currentTime = 0;
        }
      }
    }

    return () => {};
  }, [ref, isIntersecting]);


  if (!backgroundVideo?.url && !backgroundVideo?.mobile_url) {
    return null;
  }

  return (
    <div className={c.videoContainer}>
      <video
        ref={ref}
        x5-video-player-type="h5"
        playsInline
        autoPlay
        muted
        loop
        className={c.video}
        poster={cover}
        preload={cover ? 'none' : 'auto'}
        title="Faraday Future"
      >
        {!isLoadingVideo && <source type="video/mp4" />}
        {isLoadingVideo && <source src={videoSrc} type="video/mp4" />}
      </video>
    </div>
  );
}

const styles = {
  videoContainer: {
    width: '100%',
    // use doc-height from product slide show if exists, to match the height of product slide show
    height: `var(--doc-height, max(100vh, ${SLIDE_MIN_HEIGHT}px))`,
    position: 'relative',
    overflow: 'hidden',
    zIndex: '-1',
  },
  video: {
    objectFit: 'cover',
    width: '102%',
    height: '100%',
    visibility: 'inherit',
    opacity: '1',
    top: '0px',
    left: '-2px',
    display: 'block',
    // Default background - logo
    backgroundColor: 'rgb(250, 250, 250)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: '10%',
    backgroundImage: 'url(\'data:image/svg+xml;base64,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\')'
  },
};

const useStyles = createUseStyles(styles);
